import React from 'react';
import Swal from 'sweetalert2';
import {
  UNIVERSITY_ID_STATUS_OPTIONS,
  computeBatchUniversityIdCounts,
  monthsSinceBatchStart,
  normalizedUniversityIdStatus,
  buildUniversityIdExportRows,
  downloadUniversityIdTemplate,
} from '../utils/universityIdStatusHelpers';

function formatScheduleType(scheduleType) {
  if (!scheduleType) return '';
  return scheduleType.charAt(0).toUpperCase() + scheduleType.slice(1);
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function notifyNoStudents() {
  Swal.fire('Nothing to export', 'There are no students in this selection.', 'info');
}

function DownloadIconButton({ onClick, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded text-gray-400 hover:bg-emerald-50 hover:text-emerald-600"
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
      </svg>
    </button>
  );
}

// Formal per-course "batch x University ID status" headcount table — shared
// by the interactive report and its print view so both render identically.
//
// When `downloadMode` is on, every header cell and every body cell grows a
// small download button that exports the matching student(s) as an .xlsx
// sheet in the same layout the existing /admin/students/university-registration-upload
// page expects, so it can be filled in and re-uploaded to bulk-update
// registrations.universityID / registrations.universityRegisterDate.
export default function UniversityIdSummaryTable({ course, downloadMode = false }) {
  const exportCourseColumn = (statusValue) => {
    const entries = [];
    course.batches.forEach((batch) => {
      batch.students.forEach((student) => {
        if (statusValue && normalizedUniversityIdStatus(student.universityID) !== statusValue) return;
        entries.push({ student, batch });
      });
    });
    const rows = buildUniversityIdExportRows(entries);
    if (!rows.length) {
      notifyNoStudents();
      return;
    }
    const courseLabel = course.courseShortName || course.courseName || 'Course';
    downloadUniversityIdTemplate(rows, `${courseLabel}_${statusValue || 'All'}`);
  };

  const exportBatchCell = (batch, statusValue) => {
    const entries = batch.students
      .filter((s) => !statusValue || normalizedUniversityIdStatus(s.universityID) === statusValue)
      .map((student) => ({ student, batch }));
    const rows = buildUniversityIdExportRows(entries);
    if (!rows.length) {
      notifyNoStudents();
      return;
    }
    const batchLabel = batch.batchInternalName || batch.whatsappGroupName || batch.batchName || 'Batch';
    downloadUniversityIdTemplate(rows, `${batchLabel}_${statusValue || 'All'}`);
  };

  return (
    <div>
      <h3 className="mb-2 text-base font-bold text-gray-900">
        {course.courseShortName || course.courseName || 'Unknown Course'}
        {formatScheduleType(course.scheduleType) && ` (${formatScheduleType(course.scheduleType)})`}
      </h3>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] border-collapse text-xs">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-400 px-2 py-1.5 text-left font-semibold text-gray-800">Batches</th>
              <th className="border border-gray-400 px-2 py-1.5 text-center font-semibold text-gray-800">Batch Start</th>
              <th className="border border-gray-400 px-2 py-1.5 text-center font-semibold text-gray-800">Months Passed</th>
              <th className="border border-gray-400 px-2 py-1.5 text-center font-semibold text-gray-800">
                <div className="flex items-center justify-center gap-1">
                  <span>Total Students</span>
                  {downloadMode && (
                    <DownloadIconButton
                      title={`Download all students in ${course.courseShortName || course.courseName || 'this course'}`}
                      onClick={() => exportCourseColumn(null)}
                    />
                  )}
                </div>
              </th>
              {UNIVERSITY_ID_STATUS_OPTIONS.map((opt) => (
                <th key={opt.value} className="border border-gray-400 px-2 py-1.5 text-center font-semibold text-gray-800">
                  <div className="flex items-center justify-center gap-1">
                    <span>{opt.label}</span>
                    {downloadMode && (
                      <DownloadIconButton
                        title={`Download all "${opt.label}" students in ${course.courseShortName || course.courseName || 'this course'}`}
                        onClick={() => exportCourseColumn(opt.value)}
                      />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {course.batches.map((batch) => {
              const counts = computeBatchUniversityIdCounts(batch.students);
              const totalStudents = batch.students.length;
              const hasIdCount = counts.assigned || 0;
              const monthsPassed = monthsSinceBatchStart(batch.batchStartingDate);
              const isEmpty = totalStudents === 0;
              const batchLabel = batch.batchInternalName || batch.whatsappGroupName || batch.batchName || 'Batch';
              // Red: batch started 3+ months ago and not a single student has
              // a University ID yet — clearly overdue.
              const isOverdueNoIds = !isEmpty && monthsPassed !== null && monthsPassed >= 3 && hasIdCount === 0;
              // Yellow: some (but not all) students still lack a University ID.
              const isPartial = !isEmpty && !isOverdueNoIds && hasIdCount !== totalStudents;

              let rowStyle;
              if (isEmpty || isOverdueNoIds) rowStyle = { backgroundColor: '#fee2e2' };
              else if (isPartial) rowStyle = { backgroundColor: '#fef9c3' };

              return (
                <tr key={batch.batchId} style={rowStyle}>
                  <td className="border border-gray-400 px-2 py-1.5 font-semibold text-gray-800">{batchLabel}</td>
                  <td className="border border-gray-400 px-2 py-1.5 text-center">{formatDate(batch.batchStartingDate)}</td>
                  <td className="border border-gray-400 px-2 py-1.5 text-center">
                    {monthsPassed === null ? '—' : `${monthsPassed} mo`}
                  </td>
                  <td className="border border-gray-400 px-2 py-1.5 text-center font-semibold">
                    <div className="flex items-center justify-center gap-1">
                      <span>{totalStudents}</span>
                      {downloadMode && (
                        <DownloadIconButton title={`Download all students in ${batchLabel}`} onClick={() => exportBatchCell(batch, null)} />
                      )}
                    </div>
                  </td>
                  {UNIVERSITY_ID_STATUS_OPTIONS.map((opt) => (
                    <td key={opt.value} className="border border-gray-400 px-2 py-1.5 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <span>{counts[opt.value] || 0}</span>
                        {downloadMode && (
                          <DownloadIconButton
                            title={`Download "${opt.label}" students in ${batchLabel}`}
                            onClick={() => exportBatchCell(batch, opt.value)}
                          />
                        )}
                      </div>
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
